import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import miaLogo from '@/assets/images/mia-logo-dark.svg';
import appStoreIcon from '@/assets/images/app-store.svg';
import BackToTopButton from '@/components/BackToTopButton/BackToTopButton';
import bulbIcon from '@/assets/images/bulb-icon.svg';
import downArrowIcon from '@/assets/images/chevron-down.svg';
import upArrowIcon from '@/assets/images/chevron-up.svg';
import faqIcon from '@/assets/images/faq-media-icon.svg';
import BellIcon from '@/assets/images/bell-alert.svg';
// import trendingUpIcon from '@/assets/images/arrow-trending-up.svg';
import heartIcon from '@/assets/images/heart.svg';
import sparklesIcon from '@/assets/images/sparkles.svg';
import giftBrightIcon from '@/assets/images/gift-bright-future.svg';
// import roundCheckIcon from '@/assets/images/gift-sent-check.png';
import mobileMock from '@/assets/images/gift-sent-mock.jpg';
import playStoreIcon from '@/assets/images/playstore.svg';
import Heading from '@/components/Heading/Heading';
import Card from '@/components/Card/Card';
import { DetailsDisplay } from '@/components/Details/Details';
import { GIFTING_FAQS } from '@/content/gifting-faqs';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/constants';
import PageShell from '@/components/PageShell';
import { useResetStore } from '@/store';
import { blobStorage } from '@/utils/helpers/storage.helpers';

const workListItems = [
  {
    iconURL: giftBrightIcon,
    headingContent: `Gift towards their future with Mia`,
    body: (
      <p>
        Mia is an easy-to-use investment app that helps parents, grandparents,
        family and friends gift money towards a child's financial future by
        investing it in their Junior Stocks &amp; Shares <abbr>ISA</abbr>.
      </p>
    ),
  },
  {
    iconURL: BellIcon,
    headingContent: `Contribute in just a few taps`,
    body: `Once you're invited by a parent, it's quick and simple to make a contribution for birthdays, milestones and meaningful moments.`,
  },
  {
    iconURL: sparklesIcon,
    headingContent: `Turn special occasions into lasting gifts`,
    body: `Your gift can help build something meaningful for their future — from first driving lessons to a first home and beyond.`,
  },
  {
    iconURL: heartIcon,
    headingContent: `Add a message they'll treasure`,
    body: `With Mia Memories, you can leave a voice note, video or written message alongside your gift for them to discover when they turn 18.`,
  },
  // {
  //   iconURL: trendingUpIcon,
  //   headingContent: 'Keep track of your contributions',
  //   body: 'Parents can invite grandparents, family, and friends to contribute in a shared, thoughtful way.',
  // },
];

// const workListItems = [
//   {
//     iconURL: giftBrightIcon,
//     headingContent: 'Give in a way that lasts',
//     body: "Turn birthdays and special moments into support for a child's future.",
//   },
//   {
//     iconURL: BellIcon,
//     headingContent: 'Nothing to keep on top of',
//     body: 'Set reminders or regular contributions to run in the background.',
//   },
//   {
//     iconURL: sparklesIcon,
//     headingContent: 'Your care becomes part of their story',
//     body: 'Your messages are saved in Mia Memories for the child to discover at 18.',
//   },
//   {
//     iconURL: heartIcon,
//     headingContent: 'Made for the village',
//     body: 'Parents can invite grandparents, family, and friends to contribute in a shared, thoughtful way.',
//   },
//   {
//     iconURL: trendingUpIcon,
//     headingContent: 'Keep track of your contributions',
//     body: 'Parents can invite grandparents, family, and friends to contribute in a shared, thoughtful way.',
//   },
// ];

function AboutUs() {
  const { state } = useLocation();

  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('payment_id');

  const resetStore = useResetStore();

  // Mount-only: a return from the payment redirect clears the finished
  // journey once. Re-running on paymentId/resetStore changes would wipe state
  // mid-session.
  useEffect(() => {
    if (paymentId) {
      resetStore();
      blobStorage.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { message = '' } = state || {};
  const [aboutDescription, setAboutDescription] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);

  const handleToggle = (
    e: React.MouseEvent<HTMLDetailsElement>,
    index: number,
  ) => {
    e.preventDefault();
    setActiveAccordion((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* <title>
        {message || 'About Us'} | Mia Wealth - Gift your child a brighter
        financial future
      </title> */}
      <PageShell variant='single' className='about-us-page !p-0 !pt-14 !px-6'>
        <div className='gift-sent-description'>
          <header>
            {message ? (
              <Heading classes='text-2xl' align='center'>
                {message}
              </Heading>
            ) : (
              <Heading level={1} align='center'>
                <img src={miaLogo} alt='Mia' className='inline-block' />
              </Heading>
            )}
          </header>
          <main>
            <div className='app-description rounded-2xl border border-gray-300 overflow-hidden mb-4'>
              <div
                className='p-4'
                style={{
                  backgroundColor: 'oklch(0.97 0 0)',
                }}
              >
                <Heading level={2} align='center' classes='text-base font-poppins'>
                  Are you a parent?
                </Heading>
                <p className='description mb-4 text-sm'>
                  If you are a parent of a child under 16, download the Mia app
                  to open and manage a Junior Stocks and Shares{' '}
                  <abbr title='Individual Savings Account'>ISA</abbr> for your
                  own child.
                </p>
                <div className='app-download-links flex justify-center mb-4 gap-4'>
                  <a
                    href={PLAY_STORE_URL}
                    target='_blank'
                    rel='noreferrer'
                    className='download-link'
                  >
                    <img
                      src={playStoreIcon}
                      alt='Get it on Google Play'
                      className='img-fluid app-badge'
                    />
                  </a>
                  <a
                    href={APP_STORE_URL}
                    target='_blank'
                    rel='noreferrer'
                    className='download-link'
                  >
                    <img
                      src={appStoreIcon}
                      alt='Download on the App Store'
                      className='img-fluid app-badge'
                    />
                  </a>
                </div>
                <Heading level={2} align='center' classes='text-base'>
                  Just gifting?
                </Heading>
                <p className='description mb-4 text-sm'>
                  We're building a gifting app where you'll be able to see your
                  gifts, track their impact and get milestone reminders.We'll
                  let you know when it's ready.
                </p>
              </div>

              <div className='mobile-mock-wrapper'>
                <img
                  src={mobileMock}
                  alt='Gift Sent Mock'
                  className='img-fluid'
                />
              </div>
            </div>

            <div className='about-description'>
              <div className='flex justify-center mb-4'>
                <button
                  className='toggle-description flex'
                  onClick={() => setAboutDescription(!aboutDescription)}
                >
                  <span className='text-green-900 text-sm mr-2'>
                    Find out more about us
                  </span>
                  {aboutDescription ? (
                    <img
                      src={upArrowIcon}
                      alt='Hide Description'
                      className='img-fluid'
                    />
                  ) : (
                    <img
                      src={downArrowIcon}
                      alt='Show Description'
                      className='img-fluid'
                    />
                  )}
                </button>
              </div>
              {aboutDescription && (
                <>
                  <div className='works-description mb-8'>
                    <div className='title-wrapper mb-2'>
                      <img
                        src={bulbIcon}
                        alt='How it works?'
                        className='img-fluid mx-auto mb-2'
                      />
                      <Heading level={2} classes='text-xl' align='center'>
                        How it works?
                      </Heading>
                    </div>

                    <ul className='work-list'>
                      {workListItems.map((item, index) => (
                        <Card
                          key={index}
                          iconURL={item.iconURL}
                          headingContent={item.headingContent}
                          body={item.body}
                        />
                      ))}
                    </ul>
                  </div>

                  <div className='faq-content'>
                    <div className='faq-content-title text-center mb-4'>
                      <img
                        src={faqIcon}
                        alt='FAQ Icon'
                        className='img-fluid mx-auto'
                      />
                      <Heading level={3} classes='text-xl' align='center'>
                        Frequently Asked Questions
                      </Heading>
                    </div>

                    <div className='accordion'>
                      {GIFTING_FAQS.map((faq, index) => (
                        <DetailsDisplay
                          key={faq.id}
                          summaryClasses='text-sm'
                          summaryContent={
                            <h4 className='text-left text-base'>
                              {faq.question}
                            </h4>
                          }
                          openIconURL={downArrowIcon}
                          isOpen={activeAccordion === index}
                          handleToggle={(e) => handleToggle(e, index)}
                        >
                          <div className='mb-2 pt-2 text-left text-base'>
                            {faq.answer}
                          </div>
                        </DetailsDisplay>
                      ))}
                    </div>

                    <p className='site-link text-center mt-6 mb-4 font-[100]'>
                      Please visit{' '}
                      <a
                        href='http://miawealth.co.uk/support#gifting'
                        target='_blank'
                        rel='noreferrer'
                        className='text-green-900 underline'
                      >
                        www.miawealth.co.uk
                      </a>{' '}
                      for additional FAQs
                    </p>
                  </div>
                </>
              )}
            </div>
          </main>
          {aboutDescription && (
            <footer>
              <p className='end-title text-center mb-4 font-bold'>
                Download the Mia app to open an account for your child and
                invite family and friends to contribute to their future.
              </p>
              <p className='end-title text-center mb-4'>
                Want reminders for your loved ones' birthdays and a simple way
                to track contributions until they turn 18?
              </p>

              <p className='end-title text-center mb-4 font-normal'>
                We'll let you know when our app for gifters launches.
              </p>

              <div className='app-download-links flex justify-center mb-8 gap-4'>
                <a
                  href={PLAY_STORE_URL}
                  target='_blank'
                  rel='noreferrer'
                  className='download-link'
                >
                  <img
                    src={playStoreIcon}
                    alt='Get it on Google Play'
                    className='img-fluid app-badge'
                  />
                </a>
                <a
                  href={APP_STORE_URL}
                  target='_blank'
                  rel='noreferrer'
                  className='download-link'
                >
                  <img
                    src={appStoreIcon}
                    alt='Download on the App Store'
                    className='img-fluid app-badge'
                  />
                </a>
              </div>
            </footer>
          )}
        </div>

        <BackToTopButton />
      </PageShell>
    </>
  );
}

export default AboutUs;
